"use client";

import Link from "next/link";
import { useTranslations } from "next-intl";
import { FaqBrandMark } from "@/components/faq/FaqBrandMark";

const FORM_URL_PATTERN = /^https?:\/\//i;

export interface ContactPageViewProps {
  /**
   * Public Google Form URL for refunds and payment issues.
   * Override with `NEXT_PUBLIC_GRUV_CONTACT_FORM_URL` when needed.
   */
  formsUrl: string;
}

/**
 * Contact page: same floating shell as FAQ (G mark, panel, back to events),
 * support email, bug-reporting copy, and optional external form CTA.
 */
export function ContactPageView({ formsUrl }: ContactPageViewProps) {
  const t = useTranslations("contact");
  const trimmed = formsUrl.trim();
  const hasLiveForm = trimmed.length > 0 && FORM_URL_PATTERN.test(trimmed);

  return (
    <div className="gruv-faq">
      <div className="gruv-faq__panel">
        <header className="gruv-faq__header">
          <FaqBrandMark />
          <h1 className="gruv-faq__title">{t("title")}</h1>
          <p className="gruv-faq__subtitle">{t("subtitle")}</p>
        </header>

        <Link href="/events" className="gruv-faq__back">
          <span className="gruv-faq__back-arrow" aria-hidden>
            ←
          </span>
          {t("backToEvents")}
        </Link>

        <div className="gruv-faq__prose">
          <p>{t("lead")}</p>

          <div className="gruv-faq__email-block">
            <p className="gruv-faq__email-intro">{t("emailIntro")}</p>
            <a className="gruv-faq__email" href={`mailto:${t("supportEmail")}`}>
              {t("supportEmail")}
            </a>
          </div>

          <section className="gruv-faq__section" aria-labelledby="contact-bugs-heading">
            <h2 id="contact-bugs-heading" className="gruv-faq__section-title">
              {t("bugsTitle")}
            </h2>
            <p>{t("bugsBody")}</p>
            <p>{t("rewardsNote")}</p>
          </section>

          <section className="gruv-faq__section" aria-labelledby="contact-forms-heading">
            <h2 id="contact-forms-heading" className="gruv-faq__section-title">
              {t("formsTitle")}
            </h2>
            <p>{t("formsBody")}</p>
            <div className="gruv-faq__form-row">
              {hasLiveForm ? (
                <a
                  className="gruv-faq__form-cta"
                  href={trimmed}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("formsCta")}
                </a>
              ) : (
                <span className="gruv-faq__form-cta gruv-faq__form-cta--pending" role="status">
                  {t("formsCta")}
                </span>
              )}
              {!hasLiveForm ? <p className="gruv-faq__form-hint">{t("formsPlaceholderHint")}</p> : null}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
